import React, { useState, useEffect } from "react";
import { useToasts } from "react-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import EmpresaForm from "../../components/EmpresaForm";
import {
  setEmpresa,
  guardaEmpresa,
  agregaEmpresa,
} from "../../redux/empresa/empresaActions";
import { uploadImage } from "../../services/firebase/empresas/index";
import { useHistory } from "react-router-dom";

// Services for specialists
import { 
    obtenerDatosUsuarios, 
    updateUsuario, 
    createUsuario,
    obtenerEspecialidades
} from "../../services/firebase/perfilesUsuarios";
import { obtenerPerfilesCreados } from "../../services/firebase/perfiles/index";
import { usiarioUploadImage } from "../../services/firebase/usuarios/index";

// Modals
import EditUserModal from "../PerfilesUsuarios/Modales/EditarModal";

export const Empresa = ({ onSaved }) => {
  const { addToast } = useToasts();
  const dispatch = useDispatch();
  const history = useHistory();
  const empresa = useSelector((state) => state.empresa);
  const sesion = useSelector((state) => state.sesion);
  const [uploading, setUploading] = useState(false);

  // Specialists state
  const [especialistas, setEspecialistas] = useState([]);
  const [showModalEditar, setShowModalEditar] = useState(false);
  const [perfilesList, setPerfilesList] = useState([]);
  const [especialidades, setEspecialidades] = useState({});
  const [formDataUser, setFormDataUser] = useState({
    id: '',
    nombres: '',
    email: '',
    especialidad: '',
    cop: '',
    uid: '',
    perfil: '',
    empresaId: '',
    biografia: '',
    foto: '',
    firma: ''
  });

  const fetchEspecialistas = async () => {
    try {
      if (empresa.id) {
        const data = await obtenerDatosUsuarios(empresa.id);
        // Filtrar solo especialistas (Odontólogos) o todos según conveniencia
        setEspecialistas(data);
      }
    } catch (error) {
      console.error("Error fetching specialists:", error);
    }
  };

  const fetchPerfiles = async () => {
    try {
        const perfiles = await obtenerPerfilesCreados();
        setPerfilesList(perfiles);
    } catch (error) {
        console.error('Error al obtener perfiles:', error);
    }
  };

  useEffect(() => {
    fetchEspecialistas();
    fetchPerfiles();
    const fetchEspecialidadesList = async () => {
      try {
        const esp = await obtenerEspecialidades();
        setEspecialidades(esp);
      } catch (error) {
        console.error('Error al obtener especialidades:', error);
      }
    };
    fetchEspecialidadesList();
  }, [empresa.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // 1. Check Uniqueness
      const validationResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/empresas/validar-unicidad`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sesion.accessToken}`
        },
        body: JSON.stringify({
          razonSocial: empresa.razonSocial,
          slug: empresa.slug,
          currentEmpresaId: empresa.id
        })
      });

      const uniqueness = await validationResponse.json();

      if (!uniqueness.isRazonSocialUnique) {
        addToast("El nombre del consultorio ya está en uso. Por favor, elija otro.", { appearance: "warning" });
        return;
      }

      if (!uniqueness.isSlugUnique) {
        addToast("La URL amigable (Slug) ya está en uso. Por favor, elija otra.", { appearance: "warning" });
        return;
      }

      // 2. Save
      if (empresa.id) dispatch(guardaEmpresa(empresa));
      else dispatch(agregaEmpresa(empresa));

      addToast("Empresa guardada satisfactoriamente", {
        appearance: "success",
      });
      if (onSaved) onSaved();
    } catch (err) {
      addToast(err.message, { appearance: "error" });
    }
  };

  // Auto-conversion for existing companies without slug
  useEffect(() => {
    if (empresa.razonSocial && !empresa.slug) {
      const autoSlug = slugify(empresa.razonSocial);
      dispatch(setEmpresa({ ...empresa, slug: autoSlug }));
    }
  }, [empresa.id]);

  const slugify = (text) => text.toString().toLowerCase().trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newEmpresa = { ...empresa, [name]: value };
    if (name === 'razonSocial') {
      newEmpresa.slug = slugify(value);
    }
    dispatch(setEmpresa(newEmpresa));
  };

  const handlePhoneChange = (e) => {
    const phoneNumber = e.target.value.replace(/\D/g, "");
    dispatch(setEmpresa({ ...empresa, telefono: phoneNumber }));
  };

  const onFileChange = async (e) => {
    const file = e.target.files[0];
    setUploading(true);
    const fileUrl = await uploadImage(file);
    dispatch(setEmpresa({ ...empresa, logoUrl: fileUrl }));
    setUploading(false);
  };

  const handleAddressChange = ({ address, lat, lng }) => {
    dispatch(setEmpresa({ ...empresa, direccion: address, lat, lng }));
  };

  const handlePreview = () => {
    history.push('/admin/mantenimiento/diseno-landing');
  };

  // Specialist handlers
  const handleEditEspecialista = (esp) => {
    setFormDataUser({ ...esp });
    setShowModalEditar(true);
  };

  const handleNewEspecialista = () => {
    setFormDataUser({
        id: '',
        nombres: '',
        email: '',
        especialidad: '',
        cop: '',
        uid: '',
        perfil: 'ODONTOLOGO',
        empresaId: empresa.id,
        biografia: '',
        foto: '',
        firma: ''
    });
    setShowModalEditar(true);
  };

  const handleSaveUser = async () => {
    try {
        let mensaje = '';
        if (formDataUser.id) {
            await updateUsuario(formDataUser);
            mensaje = 'Especialista actualizado';
        } else {
            await createUsuario({ ...formDataUser, empresaId: empresa.id });
            mensaje = 'Especialista creado';
        }
        setShowModalEditar(false);
        addToast(mensaje, { appearance: 'success', autoDismiss: true });
        await fetchEspecialistas();
    } catch (error) {
        addToast(error.message, { appearance: 'error', autoDismiss: true });
    }
  };

  const onUserFileChange = async (e) => {
    try {
        const file = e.target.files[0];
        const fieldName = e.target.name || 'foto';
        if (file) {
            addToast('Subiendo imagen...', { appearance: 'info', autoDismiss: true });
            const fileUrl = await usiarioUploadImage(file);
            setFormDataUser(prev => ({ ...prev, [fieldName]: fileUrl }));
            addToast('Imagen cargada', { appearance: 'success', autoDismiss: true });
        }
    } catch (error) {
        addToast('Error al cargar imagen', { appearance: 'error', autoDismiss: true });
    }
  };

  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setFormDataUser(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <EmpresaForm
        empresa={empresa}
        loading={sesion.loading || sesion.loading === undefined}
        uploading={uploading}
        especialistas={especialistas}
        onChange={handleChange}
        onPhoneChange={handlePhoneChange}
        onFileChange={onFileChange}
        onAddressChange={handleAddressChange}
        onSubmit={handleSubmit}
        onPreview={handlePreview}
        onEditEspecialista={handleEditEspecialista}
        onNewEspecialista={handleNewEspecialista}
        showSubmit={true}
      />
      
      <EditUserModal
          formData={formDataUser}
          isOpen={showModalEditar}
          handleCloseModal={() => setShowModalEditar(false)}
          handleSaveUser={handleSaveUser}
          handleInputChange={handleUserInputChange}
          onFileChange={onUserFileChange}
          perfilesList={perfilesList}
          especialidades={especialidades}
      />
    </>
  );
};
